/**
 * ChessAI - AI opponent implementation with minimax algorithm
 * 
 * This class implements an AI chess player with configurable difficulty levels.
 * It uses the minimax algorithm with alpha-beta pruning for move calculation
 * and various evaluation functions to assess board positions.
 * 
 * Requirements: 12.1, 13.1, 14.1
 */

import { Board as BoardType, ChessPiece, PlayerColor, PieceType, AIDifficulty, Position } from './types';
import { Board } from './Board';

/**
 * Piece values in centipawns (1 pawn = 100)
 */
const PIECE_VALUES: Record<PieceType, number> = {
  pawn: 100,
  knight: 320,
  bishop: 330,
  rook: 500,
  queen: 900,
  king: 20000, // King is invaluable
};

/**
 * Piece-square tables for positional evaluation
 * Values are in centipawns, from white's perspective
 * Tables are indexed as [rank][file] where rank 0 = rank 1, rank 7 = rank 8
 */
const PIECE_SQUARE_TABLES = {
  pawn: [
    [0, 0, 0, 0, 0, 0, 0, 0],
    [50, 50, 50, 50, 50, 50, 50, 50],
    [10, 10, 20, 30, 30, 20, 10, 10],
    [5, 5, 10, 25, 25, 10, 5, 5],
    [0, 0, 0, 20, 20, 0, 0, 0],
    [5, -5, -10, 0, 0, -10, -5, 5],
    [5, 10, 10, -20, -20, 10, 10, 5],
    [0, 0, 0, 0, 0, 0, 0, 0],
  ],
  knight: [
    [-50, -40, -30, -30, -30, -30, -40, -50],
    [-40, -20, 0, 0, 0, 0, -20, -40],
    [-30, 0, 10, 15, 15, 10, 0, -30],
    [-30, 5, 15, 20, 20, 15, 5, -30],
    [-30, 0, 15, 20, 20, 15, 0, -30],
    [-30, 5, 10, 15, 15, 10, 5, -30],
    [-40, -20, 0, 5, 5, 0, -20, -40],
    [-50, -40, -30, -30, -30, -30, -40, -50],
  ],
  bishop: [
    [-20, -10, -10, -10, -10, -10, -10, -20],
    [-10, 0, 0, 0, 0, 0, 0, -10],
    [-10, 0, 5, 10, 10, 5, 0, -10],
    [-10, 5, 5, 10, 10, 5, 5, -10],
    [-10, 0, 10, 10, 10, 10, 0, -10],
    [-10, 10, 10, 10, 10, 10, 10, -10],
    [-10, 5, 0, 0, 0, 0, 5, -10],
    [-20, -10, -10, -10, -10, -10, -10, -20],
  ],
  rook: [
    [0, 0, 0, 0, 0, 0, 0, 0],
    [5, 10, 10, 10, 10, 10, 10, 5],
    [-5, 0, 0, 0, 0, 0, 0, -5],
    [-5, 0, 0, 0, 0, 0, 0, -5],
    [-5, 0, 0, 0, 0, 0, 0, -5],
    [-5, 0, 0, 0, 0, 0, 0, -5],
    [-5, 0, 0, 0, 0, 0, 0, -5],
    [0, 0, 0, 5, 5, 0, 0, 0],
  ],
  queen: [
    [-20, -10, -10, -5, -5, -10, -10, -20],
    [-10, 0, 0, 0, 0, 0, 0, -10],
    [-10, 0, 5, 5, 5, 5, 0, -10],
    [-5, 0, 5, 5, 5, 5, 0, -5],
    [0, 0, 5, 5, 5, 5, 0, -5],
    [-10, 5, 5, 5, 5, 5, 0, -10],
    [-10, 0, 5, 0, 0, 0, 0, -10],
    [-20, -10, -10, -5, -5, -10, -10, -20],
  ],
  king: {
    middlegame: [
      [-30, -40, -40, -50, -50, -40, -40, -30],
      [-30, -40, -40, -50, -50, -40, -40, -30],
      [-30, -40, -40, -50, -50, -40, -40, -30],
      [-30, -40, -40, -50, -50, -40, -40, -30],
      [-20, -30, -30, -40, -40, -30, -30, -20],
      [-10, -20, -20, -20, -20, -20, -20, -10],
      [20, 20, 0, 0, 0, 0, 20, 20],
      [20, 30, 10, 0, 0, 10, 30, 20],
    ],
    endgame: [
      [-50, -40, -30, -20, -20, -30, -40, -50],
      [-30, -20, -10, 0, 0, -10, -20, -30],
      [-30, -10, 20, 30, 30, 20, -10, -30],
      [-30, -10, 30, 40, 40, 30, -10, -30],
      [-30, -10, 30, 40, 40, 30, -10, -30],
      [-30, -10, 20, 30, 30, 20, -10, -30],
      [-30, -30, 0, 0, 0, 0, -30, -30],
      [-50, -30, -30, -30, -30, -30, -30, -50],
    ],
  },
};

/**
 * ChessAI class - Implements AI opponent with evaluation functions
 */
export class ChessAI {
  private difficulty: AIDifficulty;
  // Deadline (Date.now() epoch ms) for current search; null when no search is active
  private searchDeadline: number | null = null;

  constructor(difficulty: AIDifficulty = 'intermediate') {
    this.difficulty = difficulty;
  }

  /**
   * Returns true if the current search has exceeded its time budget.
   * Used by minimax to abort deep searches and return the best move found so far.
   */
  private isTimeUp(): boolean {
    return this.searchDeadline !== null && Date.now() >= this.searchDeadline;
  }

  /**
   * Evaluate material balance on the board
   * Returns score in centipawns from white's perspective
   * 
   * Requirements: 12.1, 13.1, 14.1
   */
  evaluateMaterial(board: BoardType): number {
    let score = 0;

    // Validate board structure
    if (!board || !Array.isArray(board) || board.length !== 8) {
      console.error('ChessAI: Invalid board structure in evaluateMaterial');
      return 0;
    }

    for (let rank = 0; rank < 8; rank++) {
      if (!board[rank] || !Array.isArray(board[rank])) {
        console.error(`ChessAI: Invalid rank ${rank} in evaluateMaterial`);
        continue;
      }
      for (let file = 0; file < 8; file++) {
        const piece = board[rank][file];
        if (piece) {
          const value = PIECE_VALUES[piece.type];
          score += piece.color === 'white' ? value : -value;
        }
      }
    }

    return score;
  }

  /**
   * Evaluate positional value using piece-square tables
   * Returns score in centipawns from white's perspective
   * 
   * Requirements: 12.1, 13.1, 14.1
   */
  evaluatePositional(board: BoardType): number {
    let score = 0;
    const isEndgame = this.isEndgame(board);

    for (let rank = 0; rank < 8; rank++) {
      for (let file = 0; file < 8; file++) {
        const piece = board[rank][file];
        if (!piece) continue;

        // Get the appropriate piece-square table
        let table: number[][];
        if (piece.type === 'king') {
          table = isEndgame ? PIECE_SQUARE_TABLES.king.endgame : PIECE_SQUARE_TABLES.king.middlegame;
        } else {
          table = PIECE_SQUARE_TABLES[piece.type];
        }

        // For black pieces, flip the table vertically
        const tableRank = piece.color === 'white' ? rank : 7 - rank;
        const positionalValue = table[tableRank][file];

        score += piece.color === 'white' ? positionalValue : -positionalValue;
      }
    }

    return score;
  }

  /**
   * Evaluate king safety based on pawn shield and piece proximity
   * Returns score in centipawns from white's perspective
   * 
   * Requirements: 13.1, 14.1
   */
  evaluateKingSafety(board: BoardType, player: PlayerColor): number {
    const kingPos = this.findKing(board, player);
    if (!kingPos) return 0;

    let safety = 0;

    // Evaluate pawn shield (pawns in front of king)
    const pawnShieldRanks = player === 'white' ? [1, 2] : [-1, -2];
    for (const rankOffset of pawnShieldRanks) {
      for (let fileOffset = -1; fileOffset <= 1; fileOffset++) {
        const checkRank = kingPos.rank + rankOffset;
        const checkFile = kingPos.file + fileOffset;

        if (checkRank >= 0 && checkRank < 8 && checkFile >= 0 && checkFile < 8) {
          const piece = board[checkRank][checkFile];
          if (piece && piece.type === 'pawn' && piece.color === player) {
            safety += 10; // Bonus for each pawn in shield
          }
        }
      }
    }

    // Penalty for open files near king
    for (let fileOffset = -1; fileOffset <= 1; fileOffset++) {
      const checkFile = kingPos.file + fileOffset;
      if (checkFile >= 0 && checkFile < 8) {
        let hasPawn = false;
        for (let rank = 0; rank < 8; rank++) {
          const piece = board[rank][checkFile];
          if (piece && piece.type === 'pawn' && piece.color === player) {
            hasPawn = true;
            break;
          }
        }
        if (!hasPawn) {
          safety -= 15; // Penalty for open file near king
        }
      }
    }

    // Penalty for enemy pieces near king
    for (let rankOffset = -2; rankOffset <= 2; rankOffset++) {
      for (let fileOffset = -2; fileOffset <= 2; fileOffset++) {
        const checkRank = kingPos.rank + rankOffset;
        const checkFile = kingPos.file + fileOffset;

        if (checkRank >= 0 && checkRank < 8 && checkFile >= 0 && checkFile < 8) {
          const piece = board[checkRank][checkFile];
          if (piece && piece.color !== player) {
            // Higher penalty for more powerful pieces
            if (piece.type === 'queen') safety -= 20;
            else if (piece.type === 'rook') safety -= 15;
            else if (piece.type === 'bishop' || piece.type === 'knight') safety -= 10;
          }
        }
      }
    }

    return player === 'white' ? safety : -safety;
  }

  /**
   * Evaluate pawn structure (doubled, isolated, passed pawns)
   * Returns score in centipawns from white's perspective
   * 
   * Requirements: 14.1
   */
  evaluatePawnStructure(board: BoardType, player: PlayerColor): number {
    let score = 0;

    // Analyze each file for pawn structure
    for (let file = 0; file < 8; file++) {
      const pawnsOnFile: number[] = [];
      let hasAdjacentPawn = false;

      // Find all pawns of this player on this file
      for (let rank = 0; rank < 8; rank++) {
        const piece = board[rank][file];
        if (piece && piece.type === 'pawn' && piece.color === player) {
          pawnsOnFile.push(rank);
        }
      }

      // Check for doubled pawns (penalty)
      if (pawnsOnFile.length > 1) {
        score -= 10 * (pawnsOnFile.length - 1);
      }

      // Check for isolated pawns (no friendly pawns on adjacent files)
      if (pawnsOnFile.length > 0) {
        for (const adjacentFile of [file - 1, file + 1]) {
          if (adjacentFile >= 0 && adjacentFile < 8) {
            for (let rank = 0; rank < 8; rank++) {
              const piece = board[rank][adjacentFile];
              if (piece && piece.type === 'pawn' && piece.color === player) {
                hasAdjacentPawn = true;
                break;
              }
            }
          }
        }

        if (!hasAdjacentPawn) {
          score -= 15; // Penalty for isolated pawn
        }
      }

      // Check for passed pawns (bonus)
      for (const pawnRank of pawnsOnFile) {
        let isPassed = true;
        const direction = player === 'white' ? 1 : -1;

        // Check if any enemy pawns can block or capture this pawn
        for (let checkFile = file - 1; checkFile <= file + 1; checkFile++) {
          if (checkFile < 0 || checkFile >= 8) continue;

          for (let checkRank = pawnRank + direction; 
               player === 'white' ? checkRank < 8 : checkRank >= 0; 
               checkRank += direction) {
            const piece = board[checkRank][checkFile];
            if (piece && piece.type === 'pawn' && piece.color !== player) {
              isPassed = false;
              break;
            }
          }
          if (!isPassed) break;
        }

        if (isPassed) {
          // Bonus increases as pawn advances
          const advancement = player === 'white' ? pawnRank : 7 - pawnRank;
          score += 10 + advancement * 5;
        }
      }
    }

    return player === 'white' ? score : -score;
  }

  /**
   * Combined position evaluation using all evaluation functions
   * Returns score in centipawns from white's perspective
   * Positive score favors white, negative favors black
   * 
   * Requirements: 12.1, 13.1, 14.1
   */
  evaluatePosition(board: BoardType): number {
    // Get evaluation weights based on difficulty
    const weights = this.getEvaluationWeights();

    // Calculate individual evaluation components
    const material = this.evaluateMaterial(board);
    const positional = this.evaluatePositional(board);
    const whiteSafety = this.evaluateKingSafety(board, 'white');
    const blackSafety = this.evaluateKingSafety(board, 'black');
    const kingSafety = whiteSafety + blackSafety;
    const whitePawns = this.evaluatePawnStructure(board, 'white');
    const blackPawns = this.evaluatePawnStructure(board, 'black');
    const pawnStructure = whitePawns + blackPawns;

    // Combine evaluations with difficulty-specific weights
    const score =
      material * weights.material +
      positional * weights.positional +
      kingSafety * weights.kingSafety +
      pawnStructure * weights.pawnStructure;

    return score;
  }

  /**
   * Get evaluation weights based on difficulty level
   * 
   * Beginner: Focus mostly on material
   * Intermediate: Balance material and positional play
   * Expert: Consider all factors including king safety and pawn structure
   */
  private getEvaluationWeights(): {
    material: number;
    positional: number;
    kingSafety: number;
    pawnStructure: number;
  } {
    switch (this.difficulty) {
      case 'beginner':
        return {
          material: 0.9,
          positional: 0.1,
          kingSafety: 0.0,
          pawnStructure: 0.0,
        };
      case 'intermediate':
        return {
          material: 0.7,
          positional: 0.2,
          kingSafety: 0.1,
          pawnStructure: 0.0,
        };
      case 'expert':
        return {
          material: 0.5,
          positional: 0.25,
          kingSafety: 0.15,
          pawnStructure: 0.1,
        };
    }
  }

  /**
   * Determine if the game is in endgame phase
   * Endgame is when queens are off the board or total material is low
   */
  private isEndgame(board: BoardType): boolean {
    let queens = 0;
    let totalMaterial = 0;

    for (let rank = 0; rank < 8; rank++) {
      for (let file = 0; file < 8; file++) {
        const piece = board[rank][file];
        if (piece) {
          if (piece.type === 'queen') queens++;
          if (piece.type !== 'king' && piece.type !== 'pawn') {
            totalMaterial += PIECE_VALUES[piece.type];
          }
        }
      }
    }

    // Endgame if no queens or total non-pawn material is low
    return queens === 0 || totalMaterial < 1300;
  }

  /**
   * Find the king position for a given player
   */
  private findKing(board: BoardType, player: PlayerColor): { rank: number; file: number } | null {
    for (let rank = 0; rank < 8; rank++) {
      for (let file = 0; file < 8; file++) {
        const piece = board[rank][file];
        if (piece && piece.type === 'king' && piece.color === player) {
          return { rank, file };
        }
      }
    }
    return null;
  }

  /**
   * Calculate the best move for the current position
   * Uses minimax algorithm with alpha-beta pruning and iterative deepening,
   * with strict enforcement of the provided time budget.
   *
   * Requirements: 12.1, 13.1, 14.1, 14.4
   *
   * @param engine ChessEngine instance with current game state
   * @param timeLimit Maximum time in milliseconds for calculation
   * @returns Best move found, or null if no legal moves
   */
  calculateBestMove(engine: any, timeLimit: number): { from: Position; to: Position; promotion?: PieceType } | null {
    // Set the deadline for this search; minimax checks it on each recursion
    this.searchDeadline = Date.now() + Math.max(0, timeLimit);

    try {
      // Generate all legal moves for current player
      const allMoves = this.generateAllLegalMoves(engine);

      if (allMoves.length === 0) {
        return null; // No legal moves available
      }

      // Beginner: random move (or sometimes for higher difficulties via shouldMakeMistake)
      if (this.difficulty === 'beginner' || this.shouldMakeMistake()) {
        const randomIndex = Math.floor(Math.random() * allMoves.length);
        return allMoves[randomIndex];
      }

      // Iterative deepening: search depth 1, 2, 3... until time runs out
      // or we reach the configured max depth. Always keep best move from
      // the deepest fully-completed iteration.
      const maxDepth = this.getSearchDepth();
      const orderedMoves = this.orderMoves(allMoves, engine);
      let bestMove = orderedMoves[0];

      // For intermediate, cap candidates to keep things responsive
      const candidateCount =
        this.difficulty === 'expert'
          ? orderedMoves.length
          : Math.min(15, orderedMoves.length);
      const candidates = orderedMoves.slice(0, candidateCount);

      for (let depth = 1; depth <= maxDepth; depth++) {
        // If we've already run out of time, don't start a new iteration
        if (this.isTimeUp()) {
          break;
        }

        let iterationBestMove = bestMove;
        let iterationBestScore = -Infinity;
        let iterationCompleted = true;

        for (const move of candidates) {
          if (this.isTimeUp()) {
            iterationCompleted = false;
            break;
          }

          try {
            const boardCopy = this.copyBoard(engine.getBoard());
            const tempEngine = this.createTempEngine(boardCopy, engine);
            const moveResult = tempEngine.makeMove(move.from, move.to, move.promotion);

            if (!moveResult.success) {
              continue;
            }

            const score = -this.minimax(tempEngine, depth - 1, -Infinity, Infinity, false);

            if (score > iterationBestScore) {
              iterationBestScore = score;
              iterationBestMove = move;
            }
          } catch {
            // Skip problematic moves
            continue;
          }
        }

        // Only adopt this iteration's best move if it completed before time ran out
        if (iterationCompleted) {
          bestMove = iterationBestMove;
        } else {
          // Time expired mid-iteration; keep previous depth's best move
          break;
        }
      }

      return bestMove;
    } catch {
      // Fallback to random move on unexpected errors
      const allMoves = this.generateAllLegalMoves(engine);
      if (allMoves.length === 0) return null;
      const randomIndex = Math.floor(Math.random() * allMoves.length);
      return allMoves[randomIndex];
    } finally {
      this.searchDeadline = null;
    }
  }

  /**
   * Minimax algorithm with alpha-beta pruning and time-based termination.
   *
   * If the search budget configured in {@link calculateBestMove} has been
   * exhausted, returns the static evaluation of the current position
   * immediately so the caller can fall back to the best move from a
   * shallower iteration.
   *
   * Requirements: 12.1, 13.1, 14.1, 14.4
   *
   * @param engine ChessEngine instance
   * @param depth Remaining search depth
   * @param alpha Alpha value for pruning
   * @param beta Beta value for pruning
   * @param maximizingPlayer True if maximizing, false if minimizing
   * @returns Evaluation score in centipawns
   */
  private minimax(
    engine: any,
    depth: number,
    alpha: number,
    beta: number,
    maximizingPlayer: boolean
  ): number {
    // Time-based termination: bail out with current static eval
    if (this.isTimeUp()) {
      return this.evaluatePosition(engine.getBoard().getBoard());
    }

    // Base case: reached depth limit or game over
    if (depth === 0 || this.isGameOver(engine)) {
      return this.evaluatePosition(engine.getBoard().getBoard());
    }

    const moves = this.generateAllLegalMoves(engine);

    // No legal moves - checkmate or stalemate
    if (moves.length === 0) {
      if (engine.isInCheck(engine.getCurrentPlayer())) {
        // Checkmate - return very negative score (adjusted by depth for faster mates)
        return maximizingPlayer ? -20000 + depth : 20000 - depth;
      } else {
        // Stalemate - return draw score
        return 0;
      }
    }

    // Order moves for better pruning
    const orderedMoves = this.orderMoves(moves, engine);

    if (maximizingPlayer) {
      let maxScore = -Infinity;

      for (const move of orderedMoves) {
        if (this.isTimeUp()) break;

        const boardCopy = this.copyBoard(engine.getBoard());
        const tempEngine = this.createTempEngine(boardCopy, engine);
        const moveResult = tempEngine.makeMove(move.from, move.to, move.promotion);

        if (!moveResult.success) {
          continue;
        }

        const score = this.minimax(tempEngine, depth - 1, alpha, beta, false);
        maxScore = Math.max(maxScore, score);
        alpha = Math.max(alpha, score);

        // Beta cutoff
        if (beta <= alpha) {
          break;
        }
      }

      return maxScore === -Infinity ? this.evaluatePosition(engine.getBoard().getBoard()) : maxScore;
    } else {
      let minScore = Infinity;

      for (const move of orderedMoves) {
        if (this.isTimeUp()) break;

        const boardCopy = this.copyBoard(engine.getBoard());
        const tempEngine = this.createTempEngine(boardCopy, engine);
        const moveResult = tempEngine.makeMove(move.from, move.to, move.promotion);

        if (!moveResult.success) {
          continue;
        }

        const score = this.minimax(tempEngine, depth - 1, alpha, beta, true);
        minScore = Math.min(minScore, score);
        beta = Math.min(beta, score);

        // Alpha cutoff
        if (beta <= alpha) {
          break;
        }
      }

      return minScore === Infinity ? this.evaluatePosition(engine.getBoard().getBoard()) : minScore;
    }
  }

  /**
   * Generate all legal moves for the current player
   *
   * @param engine ChessEngine instance
   * @returns Array of legal moves
   */
  private generateAllLegalMoves(engine: any): Array<{ from: Position; to: Position; promotion?: PieceType }> {
    const moves: Array<{ from: Position; to: Position; promotion?: PieceType }> = [];
    const currentPlayer = engine.getCurrentPlayer();
    const board = engine.getBoard();
    const pieces = board.getPiecesByColor(currentPlayer);

    for (const piece of pieces) {
      const legalMoves = engine.getLegalMoves(piece.position);

      for (const destination of legalMoves) {
        // Skip if destination is the same as source (invalid move)
        if (destination.file === piece.position.file && destination.rank === piece.position.rank) {
          continue;
        }

        // Handle pawn promotion
        if (piece.type === 'pawn') {
          const promotionRank = currentPlayer === 'white' ? 7 : 0;
          if (destination.rank === promotionRank) {
            // Generate all promotion options
            moves.push({ from: piece.position, to: destination, promotion: 'queen' });
            moves.push({ from: piece.position, to: destination, promotion: 'rook' });
            moves.push({ from: piece.position, to: destination, promotion: 'bishop' });
            moves.push({ from: piece.position, to: destination, promotion: 'knight' });
            continue;
          }
        }

        moves.push({ from: piece.position, to: destination });
      }
    }

    return moves;
  }

  /**
   * Order moves for better alpha-beta pruning
   * Prioritizes: captures, checks, then other moves
   * 
   * Requirements: 14.4
   * 
   * @param moves Array of moves to order
   * @param engine ChessEngine instance
   * @returns Ordered array of moves
   */
  private orderMoves(
    moves: Array<{ from: Position; to: Position; promotion?: PieceType }>,
    engine: any
  ): Array<{ from: Position; to: Position; promotion?: PieceType }> {
    const board = engine.getBoard();
    
    // Score each move for ordering
    const scoredMoves = moves.map(move => {
      let score = 0;
      
      // Prioritize captures (MVV-LVA: Most Valuable Victim - Least Valuable Attacker)
      const capturedPiece = board.getPieceAt(move.to) as ChessPiece | null;
      if (capturedPiece) {
        const movingPiece = board.getPieceAt(move.from) as ChessPiece | null;
        const attackerValue = movingPiece ? PIECE_VALUES[movingPiece.type] : 100;
        const victimValue = PIECE_VALUES[capturedPiece.type];
        score += 10 * victimValue - attackerValue;
      }
      
      // Prioritize promotions
      if (move.promotion) {
        score += PIECE_VALUES[move.promotion];
      }
      
      // Prioritize center control for non-captures
      if (!capturedPiece) {
        const centerDistance = Math.abs(move.to.file - 3.5) + Math.abs(move.to.rank - 3.5);
        score += (7 - centerDistance) * 10;
      }
      
      return { move, score };
    });
    
    // Sort by score (highest first)
    scoredMoves.sort((a, b) => b.score - a.score);
    
    return scoredMoves.map(sm => sm.move);
  }

  /**
   * Get search depth based on difficulty level
   *
   * Used as the maximum depth for iterative deepening. Time-based
   * termination in {@link minimax} ensures the search returns within
   * the configured budget regardless of this value.
   *
   * Requirements: 12.1, 13.1, 14.1
   *
   * @returns Search depth in plies
   */
  private getSearchDepth(): number {
    switch (this.difficulty) {
      case 'beginner':
        return 2;
      case 'intermediate':
        return 3;
      case 'expert':
        return 4;
    }
  }

  /**
   * Determine if AI should make a mistake based on difficulty
   * 
   * Requirements: 12.2, 13.5
   * 
   * @returns True if AI should make a suboptimal move
   */
  private shouldMakeMistake(): boolean {
    switch (this.difficulty) {
      case 'beginner':
        return Math.random() < 0.20; // 20% chance
      case 'intermediate':
        return Math.random() < 0.05; // 5% chance
      case 'expert':
        return false; // No mistakes
    }
  }

  /**
   * Check if the game is over
   * 
   * @param engine ChessEngine instance
   * @returns True if game is over
   */
  private isGameOver(engine: any): boolean {
    const status = engine.getGameStatus();
    return status.type !== 'active' && status.type !== 'check';
  }

  /**
   * Create a deep copy of the board
   * 
   * @param board Board to copy
   * @returns Deep copy of the board
   */
  private copyBoard(board: any): Board {
    // Create a new board and copy all pieces
    const newBoard = new Board();
    const originalBoard = board.getBoard();
    
    // Ensure originalBoard is valid
    if (!originalBoard || !Array.isArray(originalBoard)) {
      console.error('ChessAI: Invalid board structure in copyBoard');
      return newBoard;
    }
    
    for (let rank = 0; rank < 8; rank++) {
      if (!originalBoard[rank]) continue;
      for (let file = 0; file < 8; file++) {
        const piece = originalBoard[rank][file];
        if (piece) {
          newBoard.setPieceAt({ rank, file }, { ...piece });
        } else {
          newBoard.setPieceAt({ rank, file }, null);
        }
      }
    }
    
    return newBoard;
  }

  /**
   * Create a temporary engine for move simulation
   * 
   * @param board Board state
   * @param originalEngine Original engine to copy state from
   * @returns Temporary engine instance
   */
  private createTempEngine(board: any, originalEngine: any): any {
    // Import ChessEngine dynamically to avoid circular dependency
    const { ChessEngine } = require('./ChessEngine');
    const tempEngine = new ChessEngine();
    
    // Copy game state from original engine
    const originalState = originalEngine.getGameState();
    
    // Set the board directly on the temp engine instead of through setGameState
    // IMPORTANT: Clear position history to prevent false threefold repetition during AI simulation
    tempEngine.setGameState({
      ...originalState,
      board: board,
      positionHistory: [] // Clear position history for AI simulation
    });
    
    return tempEngine;
  }

  /**
   * Get the difficulty level
   */
  getDifficulty(): AIDifficulty {
    return this.difficulty;
  }

  /**
   * Set the difficulty level
   */
  setDifficulty(difficulty: AIDifficulty): void {
    this.difficulty = difficulty;
  }
}
